import React, { useState, useEffect, useRef } from "react";
import {
    Mail,
    Phone,
    MapPin,
    Clock,
    Send,
    MessageSquare,
    User,
    Building,
    Calendar,
    CheckCircle,
} from "lucide-react";
import {
    FaFacebookF,
    FaInstagram,
    FaLinkedinIn,
    FaWhatsapp,
    FaChevronDown,
} from "react-icons/fa";

import Navbar from "./Navbar";
import Footer from "./Footer";
import { submitEnquiry } from "../utils/submitEnquiry";
import {
    canSubmitEnquiryForm,
    sanitizeIndianMobileInput,
} from "../utils/phoneValidation";
import { openTelDialer } from "../constants/contactLinks";


export default function Contact() {
    const [contactOpen, setContactOpen] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        propertyType: "",
        message: "",
        preferredTime: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const options = ["Buying", "Selling", "Investment"];

    const [isPropertyTypeOpen, setIsPropertyTypeOpen] = useState(false);
    const [isPreferredTimeOpen, setIsPreferredTimeOpen] = useState(false);

    const propertyTypeRef = useRef(null);
    const preferredTimeRef = useRef(null);

    const handleSelect = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (field === "propertyType") setIsPropertyTypeOpen(false);
        if (field === "preferredTime") setIsPreferredTimeOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                propertyTypeRef.current &&
                !propertyTypeRef.current.contains(event.target)
            ) {
                setIsPropertyTypeOpen(false);
            }
            if (
                preferredTimeRef.current &&
                !preferredTimeRef.current.contains(event.target)
            ) {
                setIsPreferredTimeOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setIsVisible(true);
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            setFormData({
                ...formData,
                phone: sanitizeIndianMobileInput(value),
            });
            return;
        }
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const [modalForm, setModalForm] = useState({
        name: "",
        email: "",
        phone: "",
        property: "",
        message: "",
    });
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const modalFormValid = canSubmitEnquiryForm(modalForm);
    const contactPageFormValid = canSubmitEnquiryForm(formData);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmitEnquiryForm(formData)) return;

        setIsSubmitting(true);
        setIsSuccess(false);

        try {
            await submitEnquiry({
                ...formData,
                phone: sanitizeIndianMobileInput(formData.phone),
                name: formData.name.trim(),
                email: formData.email.trim(),
                propertyType: formData.propertyType || "Not specified",
                preferredTime: formData.preferredTime || "Not specified",
                source: "Contact page — Send us a message",
                submittedAt: new Date().toISOString(),
            });
            setIsSuccess(true);
            setFormData({
                name: "",
                email: "",
                phone: "",
                subject: "",
                propertyType: "",
                message: "",
                preferredTime: "",
            });
            setTimeout(() => setIsSuccess(false), 5000);
        } catch (error) {
            console.error("Contact form submission failed:", error);
            alert(error.message || "Sorry, we could not send your message. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: Phone,
            title: "Call Us",
            details: ["+91 93192 19252", "Mon-Sat: 9AM-7PM"],
            link: "tel:+919319219252",
            color: "from-blue-500 to-blue-600",
        },
        {
            icon: Mail,
            title: "Email Us",
            details: ["Info@landmarksrealty.in", "Response within 24hrs"],
            link: "mailto:Info@landmarksrealty.in",
            color: "from-purple-500 to-purple-600",
        },
        {
            icon: MapPin,
            title: "Visit Office",
            details: ["G-35, First Floor, Sector 3", "Noida, UP - 201301"],
            link: "https://maps.google.com",
            color: "from-red-500 to-red-600",
        },
        {
            icon: Clock,
            title: "Working Hours",
            details: ["Mon-Sat: 9:00 AM - 7:00 PM", "Sunday: By Appointment"],
            link: null,
            color: "from-green-500 to-green-600",
        },
    ];

    const officeFeatures = [
        { icon: Building, text: "Professional Office Space" },
        { icon: User, text: "Expert Consultation Available" },
        { icon: Calendar, text: "Schedule Site Visits" },
        { icon: MessageSquare, text: "Multilingual Support" },
    ];

    async function handleContactSubmit(e) {
        e.preventDefault();
        if (!canSubmitEnquiryForm(modalForm)) return;

        setModalSubmitting(true);
        try {
            await submitEnquiry({
                name: modalForm.name.trim(),
                email: modalForm.email.trim(),
                phone: sanitizeIndianMobileInput(modalForm.phone),
                property:
                    (modalForm.property || "").trim() || "General enquiry",
                message: (modalForm.message || "").trim(),
                source: "Contact page — Request Property Details modal",
                submittedAt: new Date().toISOString(),
            });
            setContactOpen(false);
            setModalForm({
                name: "",
                email: "",
                phone: "",
                property: "",
                message: "",
            });
        } catch (error) {
            console.error("Modal enquiry submission failed:", error);
            alert(error.message || "Sorry, we could not submit your enquiry. Please try again.");
        } finally {
            setModalSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            <Navbar />

            {/* HERO SECTION */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#3B2B9A] via-[#5A47D8] to-[#7B68EE] overflow-hidden">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-white dark:bg-slate-900 rounded-full blur-3xl animate-blob"></div>
                    <div className="absolute top-40 right-20 w-96 h-96 bg-pink-300 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
                    <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-blue-300 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
                </div>

                <div
                    className={`max-w-6xl mx-auto text-center relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                        }`}
                >
                    <div className="inline-block p-4 bg-white/20 backdrop-blur-lg rounded-2xl mb-6 shadow-xl">
                        <MessageSquare size={64} className="text-white mx-auto" />
                    </div>

                    <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
                        Let's Start a{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                            Conversation
                        </span>
                    </h1>

                    <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed font-light">
                        Whether you're buying, selling, or investing, our expert team is
                        here to guide you every step of the way
                    </p>

                    {/* Quick Stats */}
                    <div className="flex flex-wrap justify-center gap-8 mt-12">
                        <div className="text-center">
                            <div className="text-4xl font-black text-white mb-2">24hrs</div>
                            <div className="text-white/80 text-sm">Response Time</div>
                        </div>
                        <div className="w-px h-16 bg-white/30"></div>
                        <div className="text-center">
                            <div className="text-4xl font-black text-white mb-2">1200+</div>
                            <div className="text-white/80 text-sm">Happy Clients</div>
                        </div>
                        <div className="w-px h-16 bg-white/30"></div>
                        <div className="text-center">
                            <div className="text-4xl font-black text-white mb-2">9+</div>
                            <div className="text-white/80 text-sm">Years Experience</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT INFO CARDS */}
            <section className="max-w-7xl mx-auto px-6 -mt-16 relative z-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {contactInfo.map((info, idx) => (
                        <div
                            key={idx}
                            className={`bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100 dark:border-slate-700 ${isVisible
                                ? "opacity-100 translate-y-0"
                                : "opacity-0 translate-y-10"
                                }`}
                            style={{ transitionDelay: `${idx * 100}ms` }}
                        >
                            <div
                                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${info.color} text-white flex items-center justify-center mb-4 shadow-lg`}
                            >
                                <info.icon size={28} />
                            </div>
                            <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100 mb-2">
                                {info.title}
                            </h3>
                            {info.details.map((detail, i) => (
                                <p key={i} className="text-slate-600 dark:text-slate-300 text-sm">
                                    {detail}
                                </p>
                            ))}
                            {info.link && (
                                <a
                                    href={info.link}
                                    onClick={
                                        info.link.startsWith("tel:")
                                            ? openTelDialer
                                            : undefined
                                    }
                                    className="inline-flex items-center gap-2 mt-4 text-[#3B2B9A] font-semibold text-sm hover:gap-3 transition-all"
                                >
                                    Get in touch →
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* MAIN CONTENT */}
            <section className="max-w-7xl mx-auto px-6 py-20 space-y-12">
                {/* FULL-WIDTH MAP */}
                <div className="w-full bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-700 h-[450px]">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.1875825041113!2d77.31590931508072!3d28.61911298242825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5456ef36d9f%3A0x3b7191b1286a743c!2sSector%203%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Landmarks Realty Office Location"
                    ></iframe>
                </div>

                {/* FORM + RIGHT SIDE */}
                <div className="grid lg:grid-cols-2 gap-12">
                    {/* LEFT - CONTACT FORM */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 md:p-10 border border-slate-100 dark:border-slate-700">
                        <h2 className="text-3xl font-black text-slate-800 dark:text-slate-100 mb-2">
                            Send us a Message
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 mb-8">
                            Fill out the form below and we'll get back to you within 24 hours
                        </p>

                        {isSuccess && (
                            <div className="mb-6 p-4 bg-green-50 dark:bg-green-950/40 border-l-4 border-green-500 rounded-r-xl flex items-center gap-3 animate-fade-in">
                                <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
                                <div>
                                    <p className="font-semibold text-green-800 dark:text-green-200">
                                        Message Sent Successfully!
                                    </p>
                                    <p className="text-sm text-green-600 dark:text-green-400">
                                        We'll contact you soon.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Form fields */}
                            {/* Full Name & Email */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        className="lr-input"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="john@example.com"
                                        className="lr-input"
                                    />
                                </div>
                            </div>

                            {/* Phone & Property Type */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        inputMode="numeric"
                                        autoComplete="tel"
                                        maxLength={10}
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="10-digit mobile (starts 6–9)"
                                        className="lr-input"
                                    />
                                </div>
                                <div className="relative w-full" ref={propertyTypeRef}>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        I'm Interested In (optional)
                                    </label>
                                    <div
                                        className="w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-xl border border-slate-300 dark:border-slate-600 shadow-sm px-4 py-3 cursor-pointer flex justify-between items-center focus-within:ring-2 focus-within:ring-[#3B2B9A]/30 transition-all"
                                        onClick={() => setIsPropertyTypeOpen(!isPropertyTypeOpen)}
                                    >
                                        <span
                                            className={
                                                formData.propertyType
                                                    ? "text-slate-800 dark:text-slate-100"
                                                    : "text-slate-400"
                                            }
                                        >
                                            {formData.propertyType || "Select an option (optional)"}
                                        </span>
                                        <FaChevronDown
                                            className={`w-5 h-5 text-slate-500 transition-transform ${isPropertyTypeOpen ? "rotate-180" : ""
                                                }`}
                                        />
                                    </div>
                                    {isPropertyTypeOpen && (
                                        <ul className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-xl border border-slate-300 dark:border-slate-600 shadow-lg max-h-60 overflow-auto">
                                            {options.map((option) => (
                                                <li
                                                    key={option}
                                                    className="px-4 py-3 cursor-pointer text-slate-800 dark:text-slate-100 hover:bg-[#3B2B9A]/10 transition-colors"
                                                    onClick={() => handleSelect("propertyType", option)}
                                                >
                                                    {option}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Subject & Preferred Time */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        placeholder="Property Inquiry"
                                        className="lr-input"
                                    />
                                </div>
                                <div className="relative w-full" ref={preferredTimeRef}>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                        Preferred Contact Time
                                    </label>
                                    <div
                                        className="w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-xl border border-slate-300 dark:border-slate-600 shadow-sm px-4 py-3 cursor-pointer flex justify-between items-center focus-within:ring-2 focus-within:ring-[#3B2B9A]/30 transition-all"
                                        onClick={() => setIsPreferredTimeOpen(!isPreferredTimeOpen)}
                                    >
                                        <span
                                            className={
                                                formData.preferredTime
                                                    ? "text-slate-800 dark:text-slate-100"
                                                    : "text-slate-400"
                                            }
                                        >
                                            {formData.preferredTime || "Select a time"}
                                        </span>
                                        <FaChevronDown
                                            className={`w-5 h-5 text-slate-500 transition-transform ${isPreferredTimeOpen ? "rotate-180" : ""
                                                }`}
                                        />
                                    </div>
                                    {isPreferredTimeOpen && (
                                        <ul className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-xl border border-slate-300 dark:border-slate-600 shadow-lg max-h-60 overflow-auto">
                                            {[
                                                "Morning (9AM-12PM)",
                                                "Afternoon (12PM-4PM)",
                                                "Evening (4PM-7PM)",
                                            ].map((time) => (
                                                <li
                                                    key={time}
                                                    className="px-4 py-3 cursor-pointer text-slate-800 dark:text-slate-100 hover:bg-[#3B2B9A]/10 transition-colors"
                                                    onClick={() => handleSelect("preferredTime", time)}
                                                >
                                                    {time}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                    Your Message (optional)
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="5"
                                    placeholder="Tell us about your requirements..."
                                    className="lr-input resize-none"
                                ></textarea>
                            </div>

                            {/* Submit */}
                            <button
                                onClick={handleSubmit}
                                disabled={!contactPageFormValid || isSubmitting}
                                className="w-full py-4 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={20} />
                                        Send Message
                                    </>
                                )}
                            </button>

                            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                                By submitting, you agree to our{" "}
                                <a
                                    href="/privacy-policy"
                                    className="text-[#3B2B9A] hover:underline"
                                >
                                    Privacy Policy
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* RIGHT - Office Features + Social Media */}
                    <div className="space-y-8">
                        {/* Office Features */}
                        <div className="bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-3xl p-8 text-white shadow-2xl">
                            <h3 className="text-2xl font-black mb-6">
                                Why Visit Our Office?
                            </h3>
                            <div className="space-y-4">
                                {officeFeatures.map((feature, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl hover:bg-white/20 transition-all"
                                    >
                                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                                            <feature.icon size={24} />
                                        </div>
                                        <span className="font-semibold">{feature.text}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => setContactOpen(true)}
                                className="w-full mt-6 py-3 bg-white dark:bg-slate-900 text-[#3B2B9A] rounded-xl font-bold hover:shadow-lg transition-all hover:scale-[1.02]">
                                Schedule a Visit
                            </button>
                        </div>
                        {/* Social Media */}
                        {/* <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 border border-slate-100 dark:border-slate-700">
                            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 mb-4">Connect With Us</h3>
                            <p className="text-slate-600 dark:text-slate-300 mb-6">Follow us on social media for latest updates and properties</p>

                            <div className="grid grid-cols-2 gap-4">
                            </div>
                        </div> */}
                        {/* Social Media */}{" "}
                        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 border border-slate-100 dark:border-slate-700">
                            {" "}
                            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 mb-4">
                                Connect With Us
                            </h3>{" "}
                            <p className="text-slate-600 dark:text-slate-300 mb-6">
                                Follow us on social media for latest updates and properties
                            </p>{" "}
                            <div className="grid grid-cols-2 gap-4">
                                {" "}
                                <a
                                    href="https://www.facebook.com/profile.php?id=61583681855346"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all group"
                                >
                                    {" "}
                                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                        {" "}
                                        <FaFacebookF className="text-white" size={20} />{" "}
                                    </div>{" "}
                                    <div>
                                        {" "}
                                        <div className="font-bold text-slate-800 dark:text-slate-100">
                                            Facebook
                                        </div>{" "}
                                        <div className="text-xs text-slate-600 dark:text-slate-300">Follow us</div>{" "}
                                    </div>{" "}
                                </a>{" "}
                                <a
                                    href="https://www.instagram.com/landmarksrealty_?igsh=a3UzY2dwdHc3bzQ1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-4 bg-pink-50 hover:bg-pink-100 rounded-xl transition-all group"
                                >
                                    {" "}
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                        {" "}
                                        <FaInstagram className="text-white" size={20} />{" "}
                                    </div>{" "}
                                    <div>
                                        {" "}
                                        <div className="font-bold text-slate-800 dark:text-slate-100">
                                            Instagram
                                        </div>{" "}
                                        <div className="text-xs text-slate-600 dark:text-slate-300">Follow us</div>{" "}
                                    </div>{" "}
                                </a>{" "}
                                <a
                                    href="https://www.linkedin.com/company/landsmarks-realty/?viewAsMember=true"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all group"
                                >
                                    {" "}
                                    <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                        {" "}
                                        <FaLinkedinIn className="text-white" size={20} />{" "}
                                    </div>{" "}
                                    <div>
                                        {" "}
                                        <div className="font-bold text-slate-800 dark:text-slate-100">
                                            LinkedIn
                                        </div>{" "}
                                        <div className="text-xs text-slate-600 dark:text-slate-300">Connect</div>{" "}
                                    </div>{" "}
                                </a>{" "}
                                <a
                                    href="https://wa.me/919319219252"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-all group"
                                >
                                    {" "}
                                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                        {" "}
                                        <FaWhatsapp className="text-white" size={20} />{" "}
                                    </div>{" "}
                                    <div>
                                        {" "}
                                        <div className="font-bold text-slate-800 dark:text-slate-100">
                                            WhatsApp
                                        </div>{" "}
                                        <div className="text-xs text-slate-600 dark:text-slate-300">Chat now</div>{" "}
                                    </div>{" "}
                                </a>{" "}
                            </div>{" "}
                        </div>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <Footer />

            <style>{`
                @keyframes blob {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
            `}</style>

            {/* CONTACT MODAL */}
            {contactOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 opacity-100 transition-opacity duration-300"
                        onClick={() => setContactOpen(false)}
                    ></div>

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <form
                            onSubmit={handleContactSubmit}
                            className="lr-callback-panel animate-[fadeInScale_0.35s_ease-out]"
                        >
                            <button
                                type="button"
                                onClick={() => setContactOpen(false)}
                                className="absolute -top-3 -right-3 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white
                        shadow-md hover:shadow-lg w-9 h-9 flex items-center justify-center
                        rounded-full border border-white/20
                        transition-all duration-300 hover:scale-110 hover:rotate-90"
                            >
                                ✕
                            </button>

                            <h3 className="text-2xl font-extrabold text-[#2D1B7E] dark:text-indigo-200 tracking-tight">
                                Request Property Details
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                Fill in your details and we'll get back to you shortly.
                            </p>

                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    name="name"
                                    autoComplete="name"
                                    value={modalForm.name}
                                    onChange={(e) =>
                                        setModalForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="Full name"
                                    className="lr-callback-field"
                                />
                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={modalForm.email}
                                    onChange={(e) =>
                                        setModalForm((prev) => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    placeholder="Email address"
                                    className="lr-callback-field"
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={modalForm.phone}
                                    onChange={(e) =>
                                        setModalForm((prev) => ({
                                            ...prev,
                                            phone: sanitizeIndianMobileInput(
                                                e.target.value
                                            ),
                                        }))
                                    }
                                    placeholder="Mobile (10 digits, starts 6–9)"
                                    className="lr-callback-field"
                                />
                                <input
                                    name="property"
                                    value={modalForm.property}
                                    onChange={(e) =>
                                        setModalForm((prev) => ({
                                            ...prev,
                                            property: e.target.value,
                                        }))
                                    }
                                    placeholder="Property of interest (optional)"
                                    className="lr-callback-field"
                                />
                            </div>

                            <textarea
                                name="message"
                                rows="3"
                                value={modalForm.message}
                                onChange={(e) =>
                                    setModalForm((prev) => ({
                                        ...prev,
                                        message: e.target.value,
                                    }))
                                }
                                placeholder="Message (optional)"
                                className="lr-callback-field resize-none"
                            ></textarea>

                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    type="submit"
                                    disabled={!modalFormValid || modalSubmitting}
                                    className="relative overflow-hidden group px-6 py-3 rounded-xl 
                            text-white font-semibold bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8]
                            shadow-lg hover:shadow-xl hover:brightness-105 transition-all duration-300
                            disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {modalSubmitting ? "Sending..." : "Submit Request"}
                                    <span className="absolute inset-0 bg-white/20 translate-x-[-120%]
                            group-hover:translate-x-[120%] transition-all duration-700 
                            ease-in-out skew-x-[45deg]"></span>
                                </button>
                                <small className="text-gray-600 dark:text-gray-400">We respect your privacy.</small>
                            </div>
                        </form>
                    </div>

                    <style>
                        {`
                @keyframes fadeInScale {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}
                    </style>
                </>
            )}
        </div>
    );
}
